import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
// Enums
export var SummarizationStatus;
(function (SummarizationStatus) {
    SummarizationStatus["Pending"] = "pending";
    SummarizationStatus["Processing"] = "processing";
    SummarizationStatus["Completed"] = "completed";
    SummarizationStatus["Failed"] = "failed";
})(SummarizationStatus || (SummarizationStatus = {}));
// Validation schemas
export const createSummarizationRequestSchema = z.object({
    url: z.string().url('Please enter a valid URL'),
});
const summarizationResponseSchema = z.object({
    id: z.string(),
    userId: z.string(),
    url: z.string(),
    title: z.string().optional(),
    summary: z.string().optional(),
    status: z.nativeEnum(SummarizationStatus),
    errorMessage: z.string().optional(),
    retryCount: z.number(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    version: z.number(),
});
const createSummarizationResponseSchema = z.object({
    summarizationId: z.string(),
});
const getUserSummarizationsResponseSchema = z.object({
    summarizations: z.array(summarizationResponseSchema),
    page: z.number().optional(),
    limit: z.number().optional(),
    total: z.number().optional(),
});
// React Query hooks
export const useCreateSummarization = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            const validatedData = createSummarizationRequestSchema.parse(data);
            return authenticatedRequest({
                url: '/summarizations',
                method: 'POST',
                data: validatedData,
            }, createSummarizationResponseSchema);
        },
        onSuccess: () => {
            // Invalidate and refetch summarizations list
            queryClient.invalidateQueries({ queryKey: ['summarizations'] });
        },
    });
};
export const useGetUserSummarizations = (authenticatedRequest, options) => {
    const { page, limit, autoFetch = true } = options || {};
    return useQuery({
        queryKey: ['summarizations', { page, limit }],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (page !== undefined)
                params.append('page', page.toString());
            if (limit !== undefined)
                params.append('limit', limit.toString());
            const queryString = params.toString();
            const url = queryString ? `/summarizations?${queryString}` : '/summarizations';
            return authenticatedRequest({
                url,
                method: 'GET',
            }, getUserSummarizationsResponseSchema);
        },
        enabled: autoFetch,
    });
};
export const useGetSummarization = (authenticatedRequest, summarizationId, options) => {
    const { autoFetch = true } = options || {};
    if (!summarizationId) {
        return { data: undefined, isLoading: false, error: null };
    }
    return useQuery({
        queryKey: ['summarization', summarizationId],
        queryFn: async () => {
            const validatedId = z.string().min(1).parse(summarizationId);
            return authenticatedRequest({
                url: `/summarizations/${validatedId}`,
                method: 'GET',
            }, summarizationResponseSchema);
        },
        enabled: autoFetch && !!summarizationId,
    });
};
// Utility function to get status display information
export const getSummarizationStatusInfo = (status) => {
    switch (status) {
        case SummarizationStatus.Pending:
            return {
                label: 'Pending',
                color: 'text-yellow-600',
                bgColor: 'bg-yellow-50',
                borderColor: 'border-yellow-200',
                description: 'Waiting to be processed',
            };
        case SummarizationStatus.Processing:
            return {
                label: 'Processing',
                color: 'text-blue-600',
                bgColor: 'bg-blue-50',
                borderColor: 'border-blue-200',
                description: 'Currently being summarized',
            };
        case SummarizationStatus.Completed:
            return {
                label: 'Completed',
                color: 'text-green-600',
                bgColor: 'bg-green-50',
                borderColor: 'border-green-200',
                description: 'Successfully summarized',
            };
        case SummarizationStatus.Failed:
            return {
                label: 'Failed',
                color: 'text-red-600',
                bgColor: 'bg-red-50',
                borderColor: 'border-red-200',
                description: 'Failed to summarize',
            };
    }
};
